import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/services/api";
import { getAbsoluteImageUrl } from "@/utils/imageUtils";
import { trackWhatsAppClick, trackViewContent } from "@/services/metaPixel";
import ClubProgresoCard from "@/components/ClubProgresoCard";
import {
  Phone,
  MapPin,
  Instagram,
  Facebook,
  MessageCircle,
  ChevronDown,
  Cake,
  Users,
  Clock,
  Award,
  Check,
  Send,
  DollarSign,
  TrendingUp,
  ShoppingBag,
} from "lucide-react";

const WHATSAPP_NUMBER = "573196536874";
const WHATSAPP_BASE_URL = `https://api.whatsapp.com/send/?phone=${WHATSAPP_NUMBER}&type=phone_number&app_absent=0`;

// Track WhatsApp clicks from landing
const handleWhatsAppClick = (source) => {
  trackWhatsAppClick(`landing_${source}`);
};

// Header Component
const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-torta-sand/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/logo-torta-cuajada.png"
              alt="Logo"
              className="w-10 h-10 rounded-full object-cover"
            />
            <span className="font-display text-xl text-torta-dark font-semibold hidden sm:block">
              Baruj Shefa SAS
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <a href="#productos" className="text-torta-muted hover:text-torta-brown transition-colors text-sm font-medium">
              Productos
            </a>
            <a href="#historia" className="text-torta-muted hover:text-torta-brown transition-colors text-sm font-medium">
              Nuestra Historia
            </a>
            <a href="#unete" className="text-torta-muted hover:text-torta-brown transition-colors text-sm font-medium">
              Únete al Equipo
            </a>
            <a href="#pedidos" className="text-torta-muted hover:text-torta-brown transition-colors text-sm font-medium">
              Hacer Pedido
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="outline" size="sm" className="hidden sm:flex border-torta-brown text-torta-brown hover:bg-torta-brown hover:text-white">
                Acceso Vendedores
              </Button>
            </Link>
            <a href={WHATSAPP_BASE_URL} target="_blank" rel="noopener noreferrer">
              <Button size="sm" className="bg-[#25D366] hover:bg-[#128C7E] text-white">
                <MessageCircle className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">WhatsApp</span>
              </Button>
            </a>
            <button
              className="md:hidden p-2"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <div className="w-6 h-5 flex flex-col justify-between">
                <span className={`block h-0.5 bg-torta-dark transition-transform ${menuOpen ? "rotate-45 translate-y-2" : ""}`}></span>
                <span className={`block h-0.5 bg-torta-dark transition-opacity ${menuOpen ? "opacity-0" : ""}`}></span>
                <span className={`block h-0.5 bg-torta-dark transition-transform ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`}></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden py-4 border-t border-torta-sand">
            <nav className="flex flex-col gap-3">
              <a href="#productos" className="py-2 text-torta-muted hover:text-torta-brown" onClick={() => setMenuOpen(false)}>Productos</a>
              <a href="#historia" className="py-2 text-torta-muted hover:text-torta-brown" onClick={() => setMenuOpen(false)}>Nuestra Historia</a>
              <a href="#unete" className="py-2 text-torta-muted hover:text-torta-brown" onClick={() => setMenuOpen(false)}>Únete al Equipo</a>
              <a href="#pedidos" className="py-2 text-torta-muted hover:text-torta-brown" onClick={() => setMenuOpen(false)}>Hacer Pedido</a>
              <Link to="/login" className="py-2 text-torta-brown font-medium" onClick={() => setMenuOpen(false)}>Acceso Vendedores</Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section - Baruj Shefa Brand
const Hero = ({ configLanding }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Use shared utility for image URL normalization
  const getAbsoluteUrl = (url) => getAbsoluteImageUrl(url, API_URL);
  
  const heroImage = getAbsoluteUrl(configLanding?.fotos?.hero) || "/horno-fuego-new.jpg";
  
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt="Horno tradicional"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
      </div>

      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
        {/* Logo */}
        <div className="mb-6">
          <img
            src="/logo-torta-cuajada.png"
            alt="Logo Baruj Shefa"
            className="w-28 h-28 md:w-36 md:h-36 mx-auto rounded-full border-4 border-white/20 shadow-2xl object-cover"
          />
        </div>

        {/* Brand Name */}
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-white mb-3 drop-shadow-lg">
          Baruj Shefa
        </h1>
        
        {/* Tagline */}
        <p className="text-amber-300 text-lg md:text-xl font-medium tracking-wide mb-2">
          Bendición en Abundancia
        </p>
        
        {/* Subtitle */}
        <p className="text-white/80 text-sm md:text-base uppercase tracking-[0.25em] mb-8">
          Panadería Artesanal Digital
        </p>

        {/* Star Product Highlight */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl px-6 py-5 md:px-10 md:py-6 max-w-2xl mx-auto border border-white/20">
          <p className="text-white/70 text-xs uppercase tracking-widest mb-2">Producto Estrella</p>
          <h2 className="font-display text-3xl md:text-4xl text-white mb-2">
            Torta de Cuajada Artesanal
          </h2>
          <p className="text-white/80 text-sm md:text-base italic">
            &quot;Receta de la abuela, tradición que se disfruta hasta el final&quot;
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <Link 
            to="/catalogo/baruj-principal" 
            className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-4 rounded-full transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
          >
            <ShoppingBag className="w-5 h-5" />
            <span>Ver Catálogo y Ordenar</span>
          </Link>
          <a 
            href={`https://api.whatsapp.com/send/?phone=573196536874&text=${encodeURIComponent("¡Hola! Quiero hacer un pedido de Torta de Cuajada 🥮")}`} 
            target="_blank" 
            rel="noopener noreferrer" 
            onClick={() => handleWhatsAppClick('hero')}
            className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-4 rounded-full transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
          >
            <MessageCircle className="w-5 h-5" />
            <span>Pedir por WhatsApp</span>
          </a>
        </div>

        {/* Scroll indicator */}
        <div className="mt-16 animate-bounce">
          <a href="#productos" className="text-white/60 flex flex-col items-center gap-2">
            <span className="text-xs uppercase tracking-widest">Explora</span>
            <ChevronDown className="w-6 h-6" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Products Section
const productos = [
  {
    id: "personal",
    nombre: "Torta Personal",
    precio: "$7.000",
    precioNum: 7000,
    descripcion: "Porción individual perfecta para un capricho",
    image: "/torta-desayuno.jpg",
  },
  {
    id: "mediana",
    nombre: "Torta Mediana",
    precio: "$12.000",
    precioNum: 12000,
    descripcion: "Para 2-3 personas, ideal para compartir",
    image: "/torta-hero-new.jpg",
  },
  {
    id: "familiar",
    nombre: "Torta Familiar",
    precio: "$24.000",
    precioNum: 24000,
    descripcion: "Para 6-8 personas, el clásico de la abuela",
    image: "/tortas-tres-tamanos.jpg",
  },
  {
    id: "almojabanas",
    nombre: "Paquete Almojábanas x5",
    precio: "$10.000",
    precioNum: 10000,
    descripcion: "5 almojábanas recién horneadas",
    image: "/torta-desayuno.jpg",
  },
];

// Products Section - Uses prices from admin config
const ProductsSection = ({ configLanding, productosConfig }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Use shared utility for image URL normalization
  const getAbsoluteUrl = (url) => getAbsoluteImageUrl(url, API_URL);

  // Get product image by product name (normalized)
  const getProductImage = (productoNombre, productoImagen) => {
    // First check if product has its own image_url
    if (productoImagen) {
      return getAbsoluteUrl(productoImagen);
    }
    // Normalize product name to create a key
    const key = `producto_${productoNombre.toLowerCase().replace(/\s+/g, "_").replace(/á/g, "a").replace(/é/g, "e").replace(/í/g, "i").replace(/ó/g, "o").replace(/ú/g, "u")}`;
    if (configLanding?.fotos?.[key]) {
      return getAbsoluteUrl(configLanding.fotos[key]);
    }
    // Try with index-based fallback for backwards compatibility
    const index = productosConfig?.findIndex(p => p.nombre === productoNombre) || 0;
    const indexKey = `producto_${index + 1}`;
    if (configLanding?.fotos?.[indexKey]) {
      return getAbsoluteUrl(configLanding.fotos[indexKey]);
    }
    // Fallback to default images
    return "/torta-desayuno.jpg";
  };

  // Get all active products, exclude tests
  const allProducts = (productosConfig || productos).filter(p => {
    const nombre = p.nombre?.toLowerCase() || "";
    return !nombre.includes("test") && !nombre.includes("prueba") && p.activo !== false;
  });

  // Separate featured (first 3) and rotating products
  const featuredProducts = allProducts.slice(0, 3);
  const rotatingProducts = allProducts.slice(3);

  // Auto-rotate carousel
  useEffect(() => {
    if (rotatingProducts.length <= 3) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % Math.ceil(rotatingProducts.length / 3));
    }, 4000);
    
    return () => clearInterval(interval);
  }, [rotatingProducts.length]);

  // Get current visible rotating products (show 3 at a time)
  const getVisibleRotatingProducts = () => {
    if (rotatingProducts.length === 0) return [];
    const startIndex = (currentSlide * 3) % rotatingProducts.length;
    const visible = [];
    for (let i = 0; i < Math.min(3, rotatingProducts.length); i++) {
      visible.push(rotatingProducts[(startIndex + i) % rotatingProducts.length]);
    }
    return visible;
  };

  const visibleRotating = getVisibleRotatingProducts();
  const totalSlides = Math.ceil(rotatingProducts.length / 3);

  return (
    <section id="productos" className="py-20 bg-torta-cream">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <span className="text-torta-brown text-xs font-medium tracking-[0.3em] uppercase">
            Nuestras Especialidades
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-torta-dark mt-4 mb-4">
            Productos Artesanales
          </h2>
          <p className="text-torta-muted max-w-2xl mx-auto">
            Hechos con ingredientes 100% naturales: cuajada fresca, leche natural, huevos criollos. 
            Sin conservantes, horneados con amor.
          </p>
        </div>

        {/* Featured Products - 3 Destacados */}
        <div className="mb-16">
          <div className="flex items-center justify-center gap-2 mb-8">
            <Award className="w-5 h-5 text-amber-500" />
            <span className="text-amber-700 font-semibold text-sm uppercase tracking-wide">Productos Destacados</span>
          </div>
          
          {/* Main featured product (large) */}
          {featuredProducts.length > 0 && (
            <div className="mb-8">
              <div className="bg-white rounded-3xl overflow-hidden shadow-xl max-w-4xl mx-auto">
                <div className="grid md:grid-cols-2">
                  <div className="aspect-square md:aspect-auto overflow-hidden">
                    <img
                      src={getProductImage(featuredProducts[0].nombre, featuredProducts[0].imagen_url)}
                      alt={featuredProducts[0].nombre}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-8 md:p-12 flex flex-col justify-center">
                    <span className="inline-block bg-amber-100 text-amber-700 text-xs font-semibold px-3 py-1 rounded-full mb-4 w-fit">
                      ⭐ Más Vendido
                    </span>
                    <h3 className="font-display text-3xl md:text-4xl text-torta-dark mb-3">
                      {featuredProducts[0].nombre}
                    </h3>
                    <p className="text-torta-muted mb-4">
                      {featuredProducts[0].descripcion || "Nuestro producto estrella, elaborado con la receta tradicional de la abuela."}
                    </p>
                    <div className="flex items-center gap-4 mb-6">
                      <span className="font-display text-4xl text-torta-brown">
                        ${(featuredProducts[0].precio || featuredProducts[0].precioNum || 0).toLocaleString("es-CO")}
                      </span>
                    </div>
                    <Link
                      to="/catalogo/baruj-principal"
                      className="inline-flex items-center justify-center gap-2 bg-torta-brown hover:bg-torta-brown/90 text-white font-semibold px-8 py-4 rounded-full transition-all w-fit"
                    >
                      <ShoppingBag className="w-5 h-5" />
                      Ordenar Ahora
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Other 2 featured products */}
          {featuredProducts.length > 1 && (
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {featuredProducts.slice(1, 3).map((producto, idx) => (
                <div
                  key={producto.id || producto.nombre}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                      src={getProductImage(producto.nombre, producto.imagen_url)}
                      alt={producto.nombre}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-3 left-3 bg-amber-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                      ⭐ Destacado
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-2xl text-torta-dark mb-2">
                      {producto.nombre}
                    </h3>
                    <p className="text-torta-muted text-sm mb-4">{producto.descripcion || "Delicioso producto artesanal"}</p>
                    <div className="flex items-center justify-between">
                      <span className="font-display text-3xl text-torta-brown">
                        ${(producto.precio || producto.precioNum || 0).toLocaleString("es-CO")}
                      </span>
                      <Link
                        to="/catalogo/baruj-principal"
                        className="text-torta-brown hover:text-torta-dark transition-colors font-medium text-sm"
                      >
                        Ordenar →
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Rotating Products Carousel */}
        {rotatingProducts.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-center gap-2 mb-8">
              <ShoppingBag className="w-5 h-5 text-torta-brown" />
              <span className="text-torta-dark font-semibold text-sm uppercase tracking-wide">Más Productos</span>
            </div>

            {/* Carousel Container */}
            <div className="relative overflow-hidden">
              <div 
                className="grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-700 ease-in-out"
              >
                {visibleRotating.map((producto, idx) => (
                  <div
                    key={`${producto.id || producto.nombre}-${idx}`}
                    className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={getProductImage(producto.nombre, producto.imagen_url)}
                        alt={producto.nombre}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-display text-xl text-torta-dark mb-1">
                        {producto.nombre}
                      </h3>
                      <p className="text-torta-muted text-sm mb-3 line-clamp-2">
                        {producto.descripcion || "Producto artesanal de calidad"}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="font-display text-2xl text-torta-brown">
                          ${(producto.precio || producto.precioNum || 0).toLocaleString("es-CO")}
                        </span>
                        <Link
                          to="/catalogo/baruj-principal"
                          className="bg-torta-cream hover:bg-torta-sand text-torta-brown px-4 py-2 rounded-full text-sm font-medium transition-colors"
                        >
                          Ver más
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Carousel Indicators */}
              {totalSlides > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  {Array.from({ length: totalSlides }).map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentSlide(idx)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        currentSlide === idx 
                          ? "bg-torta-brown w-6" 
                          : "bg-torta-sand hover:bg-torta-brown/50"
                      }`}
                      aria-label={`Ir a slide ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* View Full Catalog CTA */}
        <div className="text-center mt-8">
          <Link 
            to="/catalogo/baruj-principal"
            className="inline-flex items-center gap-2 bg-torta-brown hover:bg-torta-brown/90 text-white font-semibold px-8 py-4 rounded-full transition-all"
          >
            <ShoppingBag className="w-5 h-5" />
            Ver Catálogo Completo y Ordenar
          </Link>
        </div>
      </div>
    </section>
  );
};

// Catalog Section - Digital Catalog CTA
const CatalogSection = ({ configLanding }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Use shared utility for image URL normalization
  const getAbsoluteUrl = (url) => getAbsoluteImageUrl(url, API_URL);
  
  const catalogoBg1 = configLanding?.fotos?.catalogo_bg_1 ? getAbsoluteUrl(configLanding.fotos.catalogo_bg_1) : null;
  const catalogoBg2 = configLanding?.fotos?.catalogo_bg_2 ? getAbsoluteUrl(configLanding.fotos.catalogo_bg_2) : null;

  return (
    <section id="catalogo" className="py-20 bg-gradient-to-br from-torta-brown via-[#5D3A1A] to-[#3D2510]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <span className="text-amber-300 text-xs font-medium tracking-[0.3em] uppercase">
            Catálogo Digital
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-white mt-4 mb-4">
            Pide Fácil y Rápido
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto">
            Explora nuestro catálogo completo, agrega productos al carrito y completa tu pedido en minutos.
            También puedes encontrar un vendedor cerca de ti.
          </p>
        </div>

        {/* Two options */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Option 1: Direct Catalog */}
          <div 
            className="relative overflow-hidden bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-colors"
          >
            {catalogoBg1 && (
              <div 
                className="absolute inset-0 opacity-15"
                style={{
                  backgroundImage: `url(${catalogoBg1})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            )}
            <div className="relative z-10">
              <div className="w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <Cake className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-display text-2xl text-white text-center mb-3">
                Catálogo Directo
              </h3>
              <p className="text-white/70 text-center text-sm mb-6">
                Haz tu pedido directamente desde nuestro catálogo digital. 
                Selecciona productos, elige entrega y paga como prefieras.
              </p>
              <Link 
                to="/catalogo/baruj-principal"
                className="flex items-center justify-center gap-2 w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-4 rounded-full transition-all"
              >
                <Cake className="w-5 h-5" />
                Ver Catálogo
              </Link>
            </div>
          </div>

          {/* Option 2: Find Seller */}
          <div 
            className="relative overflow-hidden bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-colors"
          >
            {catalogoBg2 && (
              <div 
                className="absolute inset-0 opacity-15"
                style={{
                  backgroundImage: `url(${catalogoBg2})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            )}
            <div className="relative z-10">
              <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-display text-2xl text-white text-center mb-3">
                Vendedores Autorizados
              </h3>
              <p className="text-white/70 text-center text-sm mb-6">
                Contacta a un vendedor autorizado cerca de ti. 
                Atención personalizada y entrega directa.
              </p>
              <a 
                href={`https://api.whatsapp.com/send/?phone=573196536874&text=${encodeURIComponent("¡Hola! Quiero contactar un vendedor de Baruj Shefa cerca de mi ubicación")}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleWhatsAppClick('vendedor_autorizado')}
                className="flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-4 rounded-full transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                Encontrar Vendedor
              </a>
            </div>
          </div>
        </div>

        {/* Promo Banner */}
        <div className="mt-12 bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/20 max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-14 h-14 bg-amber-500/30 rounded-xl flex items-center justify-center text-3xl flex-shrink-0">
                🎁
              </div>
              <div>
                <h3 className="font-display text-xl md:text-2xl text-white mb-2">
                  ¡Torta GRATIS en compras +$50.000!
                </h3>
                <p className="text-white/80 text-sm">
                  Por compras superiores a $50.000, recibe una torta de cuajada snack totalmente gratis. No aplica para combos o promociones.
                </p>
              </div>
            </div>
            <Link 
              to="/catalogo/baruj-principal"
              className="flex-shrink-0 bg-white text-torta-brown hover:bg-amber-100 font-bold px-6 py-3 rounded-full transition-all"
            >
              Aprovechar Oferta
            </Link>
          </div>
        </div>

        {/* Quick WhatsApp */}
        <div className="text-center mt-10">
          <p className="text-white/60 text-sm mb-3">¿Prefieres atención directa?</p>
          <a 
            href={`https://api.whatsapp.com/send/?phone=573196536874&text=${encodeURIComponent("¡Hola! Quiero hacer un pedido de Torta de Cuajada")}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleWhatsAppClick('atencion_directa')}
            className="inline-flex items-center gap-2 text-green-400 hover:text-green-300 font-medium transition-colors"
          >
            <MessageCircle className="w-5 h-5" />
            Escríbenos por WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
};

// Story Section
const StorySection = ({ configLanding }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Use shared utility for image URL normalization
  const getAbsoluteUrl = (url) => getAbsoluteImageUrl(url, API_URL);

  const getHistoryImage = (key, defaultImg) => {
    const url = configLanding?.fotos?.[key];
    return url ? getAbsoluteUrl(url) : defaultImg;
  };

  return (
    <section id="historia" className="py-20 bg-torta-sand">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-torta-brown text-xs font-medium tracking-[0.3em] uppercase">
              Nuestra Historia
            </span>
            <h2 className="font-display text-4xl md:text-5xl text-torta-dark mt-4 mb-6">
              Tradición de Chipaque
            </h2>
            <p className="text-torta-muted leading-relaxed mb-6">
              Nuestras tortas están inspiradas en la receta tradicional de Chipaque, 
              Cundinamarca, un pueblo reconocido por su auténtica torta de cuajada. 
              Hoy llevamos ese sabor tradicional desde Bogotá hasta tu mesa.
            </p>
            <p className="text-torta-muted leading-relaxed mb-6">
              Cada torta lleva consigo el amor de generaciones, el olor a canela 
              de las mañanas y el abrazo cálido de quien cocina con el corazón.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-4">
                <div className="w-10 h-10 bg-torta-cream rounded-lg flex items-center justify-center mb-3">
                  <Check className="w-5 h-5 text-torta-brown" />
                </div>
                <h4 className="font-display text-lg text-torta-dark">100% Natural</h4>
                <p className="text-torta-muted text-sm">Sin conservantes</p>
              </div>
              <div className="bg-white rounded-xl p-4">
                <div className="w-10 h-10 bg-torta-cream rounded-lg flex items-center justify-center mb-3">
                  <Clock className="w-5 h-5 text-torta-brown" />
                </div>
                <h4 className="font-display text-lg text-torta-dark">Horneo Diario</h4>
                <p className="text-torta-muted text-sm">Siempre fresquita</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <img
              src={getHistoryImage("historia_1", "/chipaque-paisaje-new.jpg")}
              alt="Chipaque"
              className="rounded-2xl shadow-lg w-full h-48 object-cover"
            />
            <img
              src={getHistoryImage("historia_2", "/ingredientes-new.jpg")}
              alt="Ingredientes"
              className="rounded-2xl shadow-lg w-full h-48 object-cover mt-8"
            />
            <img
              src={getHistoryImage("historia_3", "/torta-corte-real.jpg")}
              alt="Torta"
              className="rounded-2xl shadow-lg w-full h-48 object-cover"
            />
            <img
              src={getHistoryImage("historia_4", "/familia-preparando.jpg")}
              alt="Familia"
              className="rounded-2xl shadow-lg w-full h-48 object-cover mt-8"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Join Team Section
const beneficios = [
  { icon: DollarSign, titulo: "Gana hasta $80.000 diarios", descripcion: "Comisiones atractivas" },
  { icon: Clock, titulo: "Horario flexible", descripcion: "Tú decides cuándo trabajar" },
  { icon: TrendingUp, titulo: "Bonos por metas", descripcion: "Gana extra por objetivos" },
  { icon: Users, titulo: "Capacitación incluida", descripcion: "Te enseñamos todo" },
  { icon: MapPin, titulo: "Trabaja en tu zona", descripcion: "Elige tu ubicación" },
  { icon: Award, titulo: "Crecimiento real", descripcion: "Oportunidad de ascender" },
];

const JoinTeamSection = ({ configLanding }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Use shared utility for image URL normalization
  const getAbsoluteUrl = (url) => getAbsoluteImageUrl(url, API_URL);
  
  const uneteBg = configLanding?.fotos?.unete_bg ? getAbsoluteUrl(configLanding.fotos.unete_bg) : null;
  const uneteCtaBg = configLanding?.fotos?.unete_cta_bg ? getAbsoluteUrl(configLanding.fotos.unete_cta_bg) : null;

  return (
    <section id="unete" className="relative py-20 bg-white overflow-hidden">
      {/* Background image sutil para toda la sección */}
      {uneteBg && (
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url(${uneteBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}
      
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <span className="text-torta-brown text-xs font-medium tracking-[0.3em] uppercase">
            Únete al Equipo
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-torta-dark mt-4 mb-4">
            Sé parte de la familia
          </h2>
          <p className="text-torta-muted max-w-2xl mx-auto">
            ¿Buscas ingresos extras? Únete como vendedor y lleva el sabor tradicional 
            de Chipaque a todos los rincones de Bogotá.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {beneficios.map((b, i) => (
            <div key={i} className="bg-torta-cream rounded-xl p-5 card-hover">
              <div className="w-12 h-12 bg-torta-brown/10 rounded-xl flex items-center justify-center mb-4">
                <b.icon className="w-6 h-6 text-torta-brown" />
              </div>
              <h3 className="font-display text-lg text-torta-dark mb-1">{b.titulo}</h3>
              <p className="text-torta-muted text-sm">{b.descripcion}</p>
            </div>
          ))}
        </div>

        {/* CTA Box con imagen de fondo */}
        <div 
          className="relative overflow-hidden bg-gradient-to-r from-torta-brown to-[#A0522D] rounded-2xl p-8 text-center text-white"
        >
          {uneteCtaBg && (
            <div 
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `url(${uneteCtaBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          )}
          <div className="relative z-10">
            <h3 className="font-display text-2xl mb-4">¿Listo para empezar?</h3>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Completa tu registro en línea y en menos de 48 horas podrías estar vendiendo.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/registro-vendedor">
                <Button className="bg-white text-torta-brown hover:bg-white/90 h-12 px-6">
                  <Users className="w-5 h-5 mr-2" />
                  Registrarme como Vendedor
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 h-12 px-6">
                  Ya soy vendedor - Ingresar
                </Button>
              </Link>
            </div>
            <p className="text-white/60 text-sm mt-4">
              ¿Prefieres contactarnos directamente?{" "}
              <a
                href={`${WHATSAPP_BASE_URL}&text=${encodeURIComponent("Hola, quiero ser vendedor de Torta de Cuajada")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-white"
              >
                Escríbenos por WhatsApp
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Order Form Section with Cart
const OrderFormSection = () => {
  const [cart, setCart] = useState([]);
  const [productosLanding, setProductosLanding] = useState([]);
  const [configLanding, setConfigLanding] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [form, setForm] = useState({
    nombre: "",
    telefono: "",
    direccion: "",
    barrio: "",
    fecha: "",
    notas: "",
    metodoPago: "contraentrega",
    incluyeDomicilio: true,
  });
  const [loading, setLoading] = useState(false);
  const [pedidoEnviado, setPedidoEnviado] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");

  useEffect(() => {
    // Fetch products and config from API
    const fetchData = async () => {
      try {
        const [productosRes, configRes] = await Promise.all([
          api.get("/productos"),
          api.get("/configuracion/landing")
        ]);
        setProductosLanding(productosRes.data.filter(p => p.activo));
        setConfigLanding(configRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        // Use defaults if API fails
        setProductosLanding(productos);
      } finally {
        setLoadingConfig(false);
      }
    };
    fetchData();
  }, []);

  const DOMICILIO_BOGOTA = configLanding?.costo_domicilio || 7000;

  const addToCart = (producto) => {
    const existing = cart.find((item) => item.id === producto.id);
    if (existing) {
      setCart(cart.map((item) =>
        item.id === producto.id
          ? { ...item, cantidad: item.cantidad + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...producto, cantidad: 1 }]);
    }
  };

  const updateQuantity = (id, cantidad) => {
    if (cantidad <= 0) {
      setCart(cart.filter((item) => item.id !== id));
    } else {
      setCart(cart.map((item) =>
        item.id === id ? { ...item, cantidad } : item
      ));
    }
  };

  const removeFromCart = (id) => {
    setCart(cart.filter((item) => item.id !== id));
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.precio || item.precioNum) * item.cantidad, 0);
  const domicilio = form.incluyeDomicilio ? DOMICILIO_BOGOTA : 0;
  const total = subtotal + domicilio;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      alert("Agrega al menos un producto al carrito");
      return;
    }
    setLoading(true);

    try {
      // Save order to database with correct field names for backend
      const pedidoData = {
        cliente_nombre: form.nombre,
        cliente_telefono: form.telefono,
        cliente_direccion: form.direccion,
        cliente_barrio: form.barrio || "Sin especificar",
        items: cart.map(item => ({
          producto_id: item.id,
          producto_nombre: item.nombre,
          cantidad: item.cantidad,
          precio_unitario: item.precio || item.precioNum,
          subtotal: (item.precio || item.precioNum) * item.cantidad
        })),
        costo_domicilio: domicilio,
        notas: form.notas || null
      };

      const response = await api.post("/pedidos", pedidoData);
      setOrderNumber(response.data.id ? response.data.id.substring(0, 8).toUpperCase() : `PED-${Date.now().toString(36).toUpperCase()}`);
      setPedidoEnviado(true);
    } catch (error) {
      console.error("Error saving order:", error);
      alert("Error al registrar el pedido. Por favor intenta de nuevo.");
      setLoading(false);
      return;
    } finally {
      setLoading(false);
    }
  };

  const sendToWhatsApp = () => {
    const cartDetails = cart.map((item) =>
      `• ${item.cantidad}x ${item.nombre} - $${(item.precioNum * item.cantidad).toLocaleString("es-CO")}`
    ).join("\n");

    const message = `¡Hola! Quiero confirmar mi pedido 🎂

*Número de Pedido:* ${orderNumber}

*Productos:*
${cartDetails}
${form.incluyeDomicilio ? `• Domicilio Bogotá - $${DOMICILIO_BOGOTA.toLocaleString("es-CO")}` : ""}

*Total: $${total.toLocaleString("es-CO")}*

*Método de pago:* ${form.metodoPago === "transferencia" ? "Transferencia" : "Contra entrega"}

*Datos de entrega:*
• Nombre: ${form.nombre}
• Teléfono: ${form.telefono}
• Dirección: ${form.direccion}
• Barrio: ${form.barrio}
${form.fecha ? `• Fecha preferida: ${form.fecha}` : ""}

${form.notas ? `*Notas:* ${form.notas}` : ""}

¡Gracias! 💕`;

    window.open(`${WHATSAPP_BASE_URL}&text=${encodeURIComponent(message)}`, "_blank");
  };

  if (pedidoEnviado) {
    return (
      <section id="pedidos" className="py-20 bg-torta-cream">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="font-display text-3xl text-torta-dark mb-2">¡Pedido Registrado!</h2>
            <p className="text-torta-muted mb-4">Tu número de pedido es:</p>
            <p className="font-display text-2xl text-torta-brown mb-6">{orderNumber}</p>
            
            <div className="bg-torta-cream rounded-xl p-4 mb-6 text-left">
              <h3 className="font-medium text-torta-dark mb-2">Resumen:</h3>
              {cart.map((item) => (
                <div key={item.id} className="flex justify-between text-sm text-torta-muted">
                  <span>{item.cantidad}x {item.nombre}</span>
                  <span>${(item.precioNum * item.cantidad).toLocaleString("es-CO")}</span>
                </div>
              ))}
              {form.incluyeDomicilio && (
                <div className="flex justify-between text-sm text-torta-muted">
                  <span>Domicilio</span>
                  <span>${DOMICILIO_BOGOTA.toLocaleString("es-CO")}</span>
                </div>
              )}
              <div className="flex justify-between font-medium text-torta-dark border-t border-torta-sand mt-2 pt-2">
                <span>Total</span>
                <span>${total.toLocaleString("es-CO")}</span>
              </div>
              <p className="text-sm text-torta-muted mt-2">
                Método: {form.metodoPago === "transferencia" ? "Transferencia" : "Contra entrega"}
              </p>
            </div>

            <p className="text-torta-muted text-sm mb-6">
              Para coordinar tu entrega y confirmar detalles, envíanos un mensaje por WhatsApp:
            </p>

            {form.telefono && (
              <div className="mb-4 text-left">
                <ClubProgresoCard telefono={form.telefono} />
              </div>
            )}

            <Button
              onClick={sendToWhatsApp}
              className="w-full h-14 bg-green-600 hover:bg-green-700 text-white mb-4"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Enviar a WhatsApp para Seguimiento
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                setPedidoEnviado(false);
                setCart([]);
                setForm({
                  nombre: "",
                  telefono: "",
                  direccion: "",
                  fecha: "",
                  notas: "",
                  metodoPago: "contraentrega",
                  incluyeDomicilio: true,
                });
              }}
              className="w-full"
            >
              Hacer otro pedido
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="pedidos" className="py-20 bg-torta-cream">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <span className="text-torta-brown text-xs font-medium tracking-[0.3em] uppercase">
            Haz tu Pedido
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-torta-dark mt-4 mb-4">
            Ordena tu Torta
          </h2>
          <p className="text-torta-muted">
            Selecciona los productos que deseas y completa tu pedido
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Product Selection */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="font-display text-xl text-torta-dark">Selecciona tus productos</h3>
            {loadingConfig ? (
              <div className="text-center py-8">
                <div className="animate-pulse text-torta-brown">Cargando productos...</div>
              </div>
            ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {(productosLanding.length > 0 ? productosLanding : productos).map((p) => {
                const fotoKey = `producto_${p.id}`;
                const foto = configLanding?.fotos?.[fotoKey];
                return (
                <div
                  key={p.id}
                  className="bg-white rounded-xl p-4 shadow-sm border border-torta-sand/50 hover:border-torta-brown/50 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-torta-cream rounded-lg flex items-center justify-center overflow-hidden">
                      {foto ? (
                        <img src={foto} alt={p.nombre} className="w-full h-full object-cover" />
                      ) : (
                        <Cake className="w-8 h-8 text-torta-brown" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-display text-torta-dark">{p.nombre}</h4>
                      <p className="text-xs text-torta-muted">{p.descripcion}</p>
                      <p className="text-torta-brown font-bold mt-1">
                        ${(p.precio || p.precioNum || 0).toLocaleString("es-CO")}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={() => addToCart({ ...p, precioNum: p.precio || p.precioNum })}
                    className="w-full mt-3 bg-torta-brown hover:bg-torta-brown/90"
                  >
                    Agregar al carrito
                  </Button>
                </div>
              )})}
            </div>
            )}
          </div>

          {/* Cart and Form */}
          <div className="space-y-6">
            {/* Cart */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-display text-lg text-torta-dark mb-4 flex items-center gap-2">
                <Cake className="w-5 h-5 text-torta-brown" />
                Tu Carrito
              </h3>
              
              {cart.length === 0 ? (
                <p className="text-torta-muted text-center py-8">Tu carrito está vacío</p>
              ) : (
                <div className="space-y-3">
                  {cart.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-torta-cream/50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-torta-dark text-sm">{item.nombre}</p>
                        <p className="text-xs text-torta-muted">${(item.precio || item.precioNum).toLocaleString("es-CO")} c/u</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.id, item.cantidad - 1)}
                          className="w-8 h-8 rounded-full bg-white border border-torta-sand flex items-center justify-center text-torta-dark hover:bg-torta-sand"
                        >
                          -
                        </button>
                        <span className="w-8 text-center font-medium">{item.cantidad}</span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.id, item.cantidad + 1)}
                          className="w-8 h-8 rounded-full bg-white border border-torta-sand flex items-center justify-center text-torta-dark hover:bg-torta-sand"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Domicilio option */}
                  <label className="flex items-center justify-between p-3 bg-blue-50 rounded-lg cursor-pointer">
                    <div>
                      <p className="font-medium text-blue-800 text-sm">Domicilio Bogotá</p>
                      <p className="text-xs text-blue-600">${DOMICILIO_BOGOTA.toLocaleString("es-CO")}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={form.incluyeDomicilio}
                      onChange={(e) => setForm({ ...form, incluyeDomicilio: e.target.checked })}
                      className="w-5 h-5 rounded border-blue-300 text-blue-600"
                    />
                  </label>

                  {/* Totals */}
                  <div className="pt-3 border-t border-torta-sand space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-torta-muted">Subtotal</span>
                      <span className="text-torta-dark">${subtotal.toLocaleString("es-CO")}</span>
                    </div>
                    {form.incluyeDomicilio && (
                      <div className="flex justify-between text-sm">
                        <span className="text-torta-muted">Domicilio</span>
                        <span className="text-torta-dark">${DOMICILIO_BOGOTA.toLocaleString("es-CO")}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-display text-lg pt-2 border-t border-torta-sand">
                      <span className="text-torta-dark">Total</span>
                      <span className="text-torta-brown">${total.toLocaleString("es-CO")}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Form */}
            {cart.length > 0 && (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
                <h3 className="font-display text-lg text-torta-dark">Datos de entrega</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre completo</Label>
                  <Input
                    id="nombre"
                    required
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                    placeholder="Tu nombre"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefono">Teléfono</Label>
                  <Input
                    id="telefono"
                    type="tel"
                    required
                    value={form.telefono}
                    onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                    placeholder="300 123 4567"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="direccion">Dirección de entrega</Label>
                  <Input
                    id="direccion"
                    required
                    value={form.direccion}
                    onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                    placeholder="Calle, número, apto"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="barrio">Barrio</Label>
                  <Input
                    id="barrio"
                    required
                    value={form.barrio}
                    onChange={(e) => setForm({ ...form, barrio: e.target.value })}
                    placeholder="Ej: Chapinero, Kennedy, Usaquén"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fecha">Fecha preferida (opcional)</Label>
                  <Input
                    id="fecha"
                    type="date"
                    value={form.fecha}
                    onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Método de pago</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, metodoPago: "contraentrega" })}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        form.metodoPago === "contraentrega"
                          ? "border-torta-brown bg-torta-brown/5 text-torta-dark"
                          : "border-torta-sand bg-white text-torta-muted hover:border-torta-brown/50"
                      }`}
                    >
                      <DollarSign className="w-5 h-5 mx-auto mb-1" />
                      Contra entrega
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, metodoPago: "transferencia" })}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        form.metodoPago === "transferencia"
                          ? "border-torta-brown bg-torta-brown/5 text-torta-dark"
                          : "border-torta-sand bg-white text-torta-muted hover:border-torta-brown/50"
                      }`}
                    >
                      <Send className="w-5 h-5 mx-auto mb-1" />
                      Transferencia
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notas">Notas (opcional)</Label>
                  <Textarea
                    id="notas"
                    value={form.notas}
                    onChange={(e) => setForm({ ...form, notas: e.target.value })}
                    placeholder="Instrucciones especiales..."
                    rows={2}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || cart.length === 0}
                  className="w-full h-12 bg-torta-brown hover:bg-torta-brown/90"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Procesando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Check className="w-5 h-5" />
                      Confirmar Pedido - ${total.toLocaleString("es-CO")}
                    </span>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="bg-torta-dark py-16">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <div className="grid md:grid-cols-4 gap-8 mb-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img
              src="/logo-torta-cuajada.png"
              alt="Logo Baruj Shefa"
              className="w-12 h-12 rounded-full object-cover"
            />
            <div>
              <h3 className="font-display text-xl text-white">Baruj Shefa</h3>
              <p className="text-amber-400 text-sm">Bendición en Abundancia</p>
            </div>
          </div>
          <p className="text-white/70 leading-relaxed max-w-md mb-4">
            Panadería Artesanal Digital. Tortas de cuajada hechas con el mismo amor 
            con el que mi abuela las hacía. Receta tradicional campesina.
          </p>
          <Link 
            to="/catalogo/baruj-principal"
            className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors"
          >
            <ShoppingBag className="w-4 h-4" />
            Ver Catálogo Digital
          </Link>
        </div>

        <div>
          <h4 className="text-white font-medium mb-4">Contacto</h4>
          <div className="space-y-3">
            <a
              href={WHATSAPP_BASE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <Phone className="w-4 h-4" />
              +57 319 653 6874
            </a>
            <div className="flex items-center gap-2 text-white/70">
              <MapPin className="w-4 h-4" />
              Bogotá, Colombia
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-white font-medium mb-4">Síguenos</h4>
          <div className="flex gap-3">
            <a
              href="https://www.instagram.com/tort.adecuajada/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              aria-label="Instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61581631356010"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              aria-label="Facebook"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <a
              href="https://www.tiktok.com/@torticasdecujada"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              aria-label="TikTok"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>

      <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-white/50 text-sm">
        <p>© 2025 Baruj Shefa - Bendición en abundancia. Todos los derechos reservados.</p>
        <div className="flex gap-6">
          <a href="#" className="hover:text-white transition-colors">Política de Privacidad</a>
          <a href="#" className="hover:text-white transition-colors">Términos de Servicio</a>
        </div>
      </div>
    </div>
  </footer>
);

// WhatsApp Floating Button
const WhatsAppButton = () => (
  <a
    href={WHATSAPP_BASE_URL}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => handleWhatsAppClick('floating')}
    className="fixed bottom-6 right-6 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all z-50"
    data-testid="whatsapp-floating-btn"
  >
    <MessageCircle className="w-7 h-7 text-white" />
  </a>
);

// Main Landing Page
// Club de la Abuela Section
const ClubSection = () => {
  const [niveles, setNiveles] = useState([]);

  useEffect(() => {
    api.get("/club/config").then((r) => {
      if (r.data.activo) setNiveles(r.data.niveles || []);
    }).catch(() => {});
  }, []);

  if (niveles.length === 0) return null;

  return (
    <section id="club" className="py-20 bg-gradient-to-br from-torta-brown via-[#5D3A1A] to-[#3D2510]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
        <span className="text-[#C8902A] text-xs font-medium tracking-[0.3em] uppercase">
          Fidelidad Baruj Shefa
        </span>
        <h2 className="font-display text-3xl sm:text-4xl text-white mt-3">
          👵 Club de la Abuela
        </h2>
        <p className="text-amber-100/80 max-w-xl mx-auto mt-3 text-sm sm:text-base">
          Cada compra te acerca más a la mesa de la abuela. Sube de nivel con tus compras y desbloquea regalos y descuentos hechos con amor.
        </p>
        <div className="grid sm:grid-cols-3 gap-4 mt-10 max-w-3xl mx-auto">
          {niveles.map((n) => (
            <div key={n.id} className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/10">
              <span className="text-4xl">{n.emoji}</span>
              <p className="font-display text-lg text-white mt-2">{n.nombre}</p>
              <p className="text-xs text-amber-100/70 mt-1">
                {n.min_compras} {n.min_compras === 1 ? "compra" : "compras"} o ${Number(n.min_facturacion).toLocaleString("es-CO")}
              </p>
              <p className="text-xs text-[#e0b050] font-semibold mt-2">🎁 {n.recompensa?.descripcion}</p>
            </div>
          ))}
        </div>
        <Link
          to="/club"
          data-testid="landing-club-cta"
          className="inline-block mt-10 bg-[#C8902A] hover:bg-[#b07e22] text-white font-semibold px-8 py-3.5 rounded-full transition-colors"
        >
          Consultar mi nivel con mi celular
        </Link>
      </div>
    </section>
  );
};

export default function LandingPage() {
  const [configLanding, setConfigLanding] = useState(null);
  const [productosConfig, setProductosConfig] = useState([]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [configRes, productosRes] = await Promise.all([
          api.get("/configuracion/landing"),
          api.get("/comisiones/productos-landing")
        ]);
        setConfigLanding(configRes.data);
        setProductosConfig(productosRes.data);
      } catch (error) {
        console.error("Error fetching landing config:", error);
      }
    };
    fetchConfig();
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <Hero configLanding={configLanding} />
      <ProductsSection configLanding={configLanding} productosConfig={productosConfig} />
      <CatalogSection configLanding={configLanding} />
      <StorySection configLanding={configLanding} />
      <ClubSection />
      <JoinTeamSection configLanding={configLanding} />
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
